import { mountTimelineDock } from './timeline-stack.js';

// Timeline nowcast condivisa v2 — port 1:1 della semantica WSNowcastTimeline
// v1 (map-layers.js): asse canonico 5', osservazioni−120'/forecast+60', un cursore per
// radar+satellite+temporali (i fulmini restano live ma ricevono showAtTime per
// lo scrub nel passato). UI ricostruita: dock flottante in basso.
const STEP_MIN = 5;
const PAST_MIN = 120; // richiesta Giuseppe: copre tutti i frame VMI osservati (~110')
const MAX_FWD_MIN = 60;
const PLAY_MS = 650;

const layers = new Map(); // id -> renderAtTime(T)
const forecastHorizons = new Map();
const dataClocks = new Map(); // id -> issue/base time; mai sostituito dal clock client
let times = [];
let index = 0;
let nowIndex = 0;
let playTimer = null;
let dock = null;

function buildGrid() {
    const available = [...dataClocks.values()].filter(Number.isFinite);
    // Il cursore live segue la sorgente più fresca. Ogni layer applica poi il
    // proprio limite reale (es. il satellite si ferma al latest_available),
    // quindi una sorgente più lenta non trattiene il radar di uno scatto.
    const t = available.length ? Math.max(...available) : Date.now();
    const now = t - (t % (STEP_MIN * 60000));
    times = [];
    // A newer observation cannot extend an older provider's forecast endpoint.
    const forwardMinutes = Math.min(MAX_FWD_MIN, Math.max(0, ...[...forecastHorizons.entries()]
        .filter(([id]) => layers.has(id)).map(([id, minutes]) => {
            const clock = dataClocks.get(id);
            const sourceTime = Number.isFinite(clock) ? clock : now;
            return Math.floor(((sourceTime + minutes * 60000 - now) / 60000) / STEP_MIN) * STEP_MIN;
        })));
    for (let m = -PAST_MIN; m <= forwardMinutes; m += STEP_MIN) times.push(now + m * 60000);
    nowIndex = times.indexOf(now);
}

const HHMM = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });
function formatLabel(T) {
    const hhmm = HHMM.format(new Date(T));
    const rel = Math.round((T - times[nowIndex]) / 60000);
    if (rel === 0) return `<b class="now">${layers.size === 1 && layers.has('lightning') ? 'LIVE' : 'ULTIMA OSS.'}</b> ${hhmm}`;
    if (rel < 0) return `<b class="past">${rel} min</b> ${hhmm}`;
    return `<b class="future">STIMA +${rel} min</b> ${hhmm}`;
}

function apply() {
    if (!times.length) return;
    index = Math.max(0, Math.min(index, times.length - 1));
    const T = times[index];
    layers.forEach(fn => { try { fn(T); } catch (e) { /* layer non pronto */ } });
    if (!dock) return;
    const range = dock.querySelector('input[type=range]');
    const label = dock.querySelector('.wsv2-nt-label');
    range.value = String(index);
    range.setAttribute('aria-valuetext', `${HHMM.format(new Date(T))}${index > nowIndex ? ' · stima futura' : index < nowIndex ? ' · osservazione passata' : ' · ultima osservazione'}`);
    range.style.setProperty('--now-pct', `${(nowIndex / Math.max(1, times.length - 1)) * 100}%`);
    label.innerHTML = formatLabel(T);
}

function pause() {
    if (playTimer) clearInterval(playTimer);
    playTimer = null;
    const button = dock?.querySelector('.wsv2-nt-play');
    button?.replaceChildren('▶');
    button?.setAttribute('aria-label', 'Avvia animazione');
    button?.setAttribute('aria-pressed', 'false');
}
function play() {
    if (playTimer || times.length < 2) return;
    dock.querySelector('.wsv2-nt-play').replaceChildren('⏸');
    dock.querySelector('.wsv2-nt-play').setAttribute('aria-label', 'Pausa animazione');
    dock.querySelector('.wsv2-nt-play').setAttribute('aria-pressed', 'true');
    playTimer = setInterval(() => { index = (index + 1) % times.length; apply(); }, PLAY_MS);
}

function ensureDock() {
    if (dock) { dock.hidden = false; return; }
    dock = document.createElement('div');
    dock.className = 'wsv2-panel wsv2-nt wsv2-nowcast-timeline';
    dock.innerHTML = `
        <button class="wsv2-nt-play" type="button" title="Avvia animazione" aria-label="Avvia animazione" aria-pressed="false">▶</button>
        <input type="range" min="0" max="0" value="0" aria-label="Timeline nowcast">
        <span class="wsv2-nt-label"></span>
        <button class="wsv2-nt-live" type="button" title="Torna all’ultima osservazione" aria-label="Torna all’ultima osservazione">●</button>`;
    if (document.documentElement.classList.contains('wsv2-embed-playback')) {
        for (const [step, label, icon] of [[-1, 'Immagine precedente', '‹'], [1, 'Immagine successiva', '›']]) {
            const button = document.createElement('button');
            button.textContent = icon;
            button.setAttribute('aria-label', label);
            button.title = label;
            button.addEventListener('click', () => { pause(); index += step; apply(); });
            dock.insertBefore(button, dock.querySelector('input'));
        }
    }
    mountTimelineDock(dock, { order: 1 }); // sotto la timeline osservazioni/previsioni
    const range = dock.querySelector('input');
    range.addEventListener('input', () => { pause(); index = Number(range.value) || 0; apply(); });
    dock.querySelector('.wsv2-nt-play').addEventListener('click', () => (playTimer ? pause() : play()));
    dock.querySelector('.wsv2-nt-live').addEventListener('click', () => { pause(); index = nowIndex; apply(); });
}

function configure() {
    ensureDock();
    const range = dock.querySelector('input');
    range.max = String(Math.max(0, times.length - 1));
    range.value = String(index);
}

function rebuildKeepingSelection() {
    const wasAtNow = index === nowIndex;
    const selected = times[index];
    buildGrid();
    index = wasAtNow || !Number.isFinite(selected) ? nowIndex : times.reduce((best, candidate, candidateIndex) =>
        Math.abs(candidate - selected) < Math.abs(times[best] - selected) ? candidateIndex : best, 0);
    configure();
    apply();
}

export const NowcastTimeline = {
    setForecastHorizon(id, minutes) {
        const horizon = Math.max(0, Math.min(MAX_FWD_MIN, Number(minutes) || 0));
        if (forecastHorizons.get(id) === horizon) return;
        forecastHorizons.set(id, horizon);
        if (layers.size) rebuildKeepingSelection();
    },
    clearForecastHorizon(id) {
        forecastHorizons.delete(id);
        if (layers.size) rebuildKeepingSelection();
    },
    setDataClock(id, value) {
        const time = Number(value);
        if (!Number.isFinite(time)) return;
        if (dataClocks.get(id) === time) return;
        dataClocks.set(id, time);
        if (layers.size) rebuildKeepingSelection();
    },
    clearDataClock(id) {
        dataClocks.delete(id);
        if (layers.size) rebuildKeepingSelection();
    },
    referenceTime() {
        return times.length ? times[nowIndex] : Date.now();
    },
    register(id, renderFn) {
        const first = layers.size === 0;
        if (first) document.dispatchEvent(new CustomEvent('wsv2:nowcast-active', { detail: { active: true } }));
        layers.set(id, renderFn);
        if (first || !times.length) { buildGrid(); index = nowIndex; configure(); apply(); }
        else rebuildKeepingSelection();
    },
    unregister(id) {
        layers.delete(id);
        forecastHorizons.delete(id);
        dataClocks.delete(id);
        if (layers.size === 0) {
            pause();
            if (dock) dock.hidden = true;
            document.dispatchEvent(new CustomEvent('wsv2:nowcast-active', { detail: { active: false } }));
        }
        else rebuildKeepingSelection();
    },
    notifyDataUpdated() {
        if (!layers.size) return;
        rebuildKeepingSelection();
    },
    isActive() { return layers.size > 0; },
    currentTime() { return times.length ? times[index] : Date.now(); },
};
